<template>
    <div>
        <div
            v-if="!submitted"
            id="support-form"
            class="hubspot-form-container"
        ></div>
        <div
            v-else
            ref="confirmation"
            role="status"
            aria-live="polite"
            tabindex="-1"
            class="mt-6 flex flex-col items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-6 py-8 text-center focus:outline-none focus:ring-2 focus:ring-emerald-400 dark:border-emerald-800 dark:bg-emerald-900/20"
        >
            <div
                class="flex items-center justify-center text-emerald-500 dark:text-emerald-400"
            >
                <Icon icon="circle-check" svgClass="w-10 h-10" />
            </div>
            <h3
                class="text-xl font-semibold text-slate-800 dark:text-slate-100"
            >
                Ticket Submitted
            </h3>
            <p class="text-slate-600 dark:text-slate-300 max-w-md">
                Your support ticket has been submitted successfully. Our team
                will follow up with you as soon as possible.
            </p>
        </div>
    </div>
</template>
<script>
import Icon from '@/components/ui/icon/icon.vue';

const darkIframeCss = `
    body, label, legend, p, .hs-richtext, .hs-form-field > label {
        color: #e2e8f0 !important;
    }
    input[type="text"], input[type="email"], input[type="tel"],
    input[type="number"], input[type="url"], input[type="file"],
    select, textarea {
        background-color: #334155 !important;
        color: #f1f5f9 !important;
        border-color: #475569 !important;
    }
    /*
     * The "Choose file" control is a shadow pseudo-element, so the rule above
     * paints the field around it but leaves the button itself light.
     */
    input[type="file"]::file-selector-button {
        background-color: #475569 !important;
        color: #f1f5f9 !important;
        border-color: #64748b !important;
    }
    input::placeholder, textarea::placeholder { color: #94a3b8 !important; }
    select { color-scheme: dark; }
`;

function injectDarkModeIntoHubspotIframes(container) {
    const isDark = document.documentElement.classList.contains('dark');
    if (!isDark) return;
    const iframes = container.querySelectorAll('iframe');
    iframes.forEach((iframe) => {
        try {
            const doc =
                iframe.contentDocument || iframe.contentWindow?.document;
            if (!doc) return;
            if (doc.getElementById('my10x-hubspot-dark-css')) return;
            const style = doc.createElement('style');
            style.id = 'my10x-hubspot-dark-css';
            style.textContent = darkIframeCss;
            (doc.head || doc.documentElement).appendChild(style);
        } catch {
            // cross-origin (e.g. recaptcha) — ignore
        }
    });
}

export default {
    components: { Icon },
    data() {
        return {
            submitted: false,
        };
    },
    mounted() {
        const container = this.$el.querySelector('#support-form') || this.$el;
        const script = document.createElement('script');
        script.src = 'https://js.hsforms.net/forms/v2.js';
        document.body.appendChild(script);
        script.addEventListener('load', () => {
            if (window.hbspt) {
                window.hbspt.forms.create({
                    region: 'na1',
                    portalId: '22692776',
                    formId: '5108020e-e3e0-47a0-ae89-37caf784c1f3',
                    target: '#support-form',
                    // HubSpot fires onFormSubmitted only after the submission has
                    // been accepted by their backend (post-validation, post-
                    // reCAPTCHA). onFormSubmit fires earlier on button click and
                    // is not a reliable confirmation of success — SUPPORT-731
                    // explicitly requires the message to appear only after the
                    // ticket is actually submitted, so we only listen here.
                    onFormSubmitted: () => this.handleFormSubmitted(),
                });
            }
        });

        this._hubspotObserver = new MutationObserver(() => {
            injectDarkModeIntoHubspotIframes(container);
        });
        this._hubspotObserver.observe(container, {
            childList: true,
            subtree: true,
        });
        this._hubspotInterval = setInterval(() => {
            injectDarkModeIntoHubspotIframes(container);
        }, 500);
        setTimeout(() => {
            if (this._hubspotInterval) {
                clearInterval(this._hubspotInterval);
                this._hubspotInterval = null;
            }
        }, 10000);
    },
    beforeUnmount() {
        this.teardownHubspotObservers();
    },
    methods: {
        // HubSpot has been observed to invoke onFormSubmitted more than once in
        // some rare flows (e.g. after a re-render). The guard prevents flipping
        // the state again once we already committed to the success view.
        handleFormSubmitted() {
            if (this.submitted) return;
            this.submitted = true;
            this.teardownHubspotObservers();
            this.$nextTick(() => {
                if (this.$refs.confirmation) {
                    this.$refs.confirmation.focus();
                }
            });
        },
        teardownHubspotObservers() {
            if (this._hubspotObserver) {
                this._hubspotObserver.disconnect();
                this._hubspotObserver = null;
            }
            if (this._hubspotInterval) {
                clearInterval(this._hubspotInterval);
                this._hubspotInterval = null;
            }
        },
    },
};
</script>
<style scoped>
.hubspot-form-container {
    min-height: 800px;
}

.hubspot-form-container :deep(iframe) {
    min-height: 800px !important;
    height: auto !important;
}

:where(.dark) .hubspot-form-container :deep(label),
:where(.dark) .hubspot-form-container :deep(legend),
:where(.dark) .hubspot-form-container :deep(.hs-form-field > label),
:where(.dark) .hubspot-form-container :deep(.hs-richtext),
:where(.dark) .hubspot-form-container :deep(p) {
    color: #e2e8f0 !important;
}

:where(.dark) .hubspot-form-container :deep(input[type='text']),
:where(.dark) .hubspot-form-container :deep(input[type='email']),
:where(.dark) .hubspot-form-container :deep(input[type='tel']),
:where(.dark) .hubspot-form-container :deep(input[type='number']),
:where(.dark) .hubspot-form-container :deep(input[type='url']),
:where(.dark) .hubspot-form-container :deep(input[type='file']),
:where(.dark) .hubspot-form-container :deep(select),
:where(.dark) .hubspot-form-container :deep(textarea) {
    background-color: #334155 !important;
    color: #f1f5f9 !important;
    border-color: #475569 !important;
}

/*
 * The "Choose file" control is a shadow pseudo-element, so the rule above
 * paints the field around it but leaves the button itself light.
 */
:where(.dark)
    .hubspot-form-container
    :deep(input[type='file']::file-selector-button) {
    background-color: #475569 !important;
    color: #f1f5f9 !important;
    border-color: #64748b !important;
}

:where(.dark) .hubspot-form-container :deep(input::placeholder),
:where(.dark) .hubspot-form-container :deep(textarea::placeholder) {
    color: #94a3b8 !important;
}

:where(.dark) .hubspot-form-container :deep(select) {
    color-scheme: dark;
}
</style>
